import React from 'react';
import {
    Grid,
    InputLabel,
    Slider,
    styled,
    TextField
} from '@mui/material';
import { debounce } from 'lodash';

type PropsType = {
    name: string,
    label: string,
    lowValue: number,
    highValue: number,
    minValue: number,
    maxValue: number,
    step?: number,
    required?: boolean,
    onChange: (values: [number | null, number | null]) => void
};

const StyledSlider = styled(Slider)({
    '& .MuiSlider-valueLabel': {
        top: 55,
        '&:before': { top: -7 },
    },
});
export default function RangeField(props: PropsType) {
    const {
        name,
        label,
        lowValue,
        highValue,
        minValue,
        maxValue,
        step,
        onChange,
        required
    } = props;

    const [value, setValue] = React.useState<number[]>([Math.max(lowValue, minValue), Math.min(highValue, maxValue)]);

    React.useEffect(() => {
        setValue([Math.max(lowValue, minValue), Math.min(highValue, maxValue)]);
    }, [lowValue, highValue, minValue, maxValue]);

    const handleChange = React.useCallback((event, val) => {
        setValue(val);
    }, []);

    const commitChange = React.useCallback((event, val) => {
        onChange([val[0] === minValue ? null : val[0], val[1] === maxValue ? null : val[1]]);
    }, [minValue, maxValue, onChange]);

    const stepValue = React.useMemo(() => (
        step || Math.round((maxValue - minValue) / Math.min(100, maxValue - minValue))
    ), [minValue, maxValue, step]);

    const debouncedOnChange = React.useMemo(() => debounce((newMin: number | null, newMax: number | null) => {
        onChange([newMin, newMax]);
    }, 100), [onChange]);

    const handleInputChange = (index: 0 | 1, inputValue: string) => {
        const numericValue = Number(inputValue);
        const newValue = [...value] as [number, number];

        if (index === 0) {
            newValue[0] = Math.min(numericValue, newValue[1]);
        } else {
            newValue[1] = Math.max(numericValue, newValue[0]);
        }

        newValue[0] = Math.max(minValue, newValue[0]);
        newValue[1] = Math.min(maxValue, newValue[1]);

        setValue(newValue);
        debouncedOnChange(newValue[0] === minValue ? null : newValue[0], newValue[1] === maxValue ? null : newValue[1]);
    };

    return (
        <>
            <InputLabel>
                {`${label}${required ? ' *' : ''}`}
            </InputLabel>
            <div style={{
                paddingBottom: '20px',
                paddingLeft: '30px',
                paddingRight: '30px'
            }}
            >
                <StyledSlider
                    name={name}
                    value={value}
                    max={maxValue}
                    min={minValue}
                    step={stepValue}
                    onChange={handleChange}
                    onChangeCommitted={commitChange}
                    valueLabelDisplay="off"
                />

                <Grid container spacing={2} justifyContent="space-between">
                    <Grid item>
                        <TextField
                            label="Min"
                            type="number"
                            size="small"
                            value={value[0] === minValue ? '' : value[0]}
                            onChange={(e) => handleInputChange(0, e.target.value)}
                            // onBlur={commitChange}
                            inputProps={{
                                step: stepValue,
                                min: minValue,
                                max: value[1]
                            }}
                            style={{ width: 100 }}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            label="Max"
                            type="number"
                            size="small"
                            value={value[1] === maxValue ? '' : value[1]}
                            onChange={(e) => handleInputChange(1, e.target.value)}
                            // onBlur={commitChange}
                            inputProps={{
                                step: stepValue,
                                min: value[0],
                                max: maxValue
                            }}
                            style={{ width: 100 }}
                        />
                    </Grid>
                </Grid>
            </div>
        </>
    );
}
